import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Star, Sun, Heart, Coins } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API } from '../utils/helpers';
import { ShareButton } from '../components/ShareComponents';
import MarkdownRenderer from '../components/MarkdownRenderer';
import NatalChartWheel from '../components/NatalChartWheel';
import AspectsTable from '../components/AspectsTable';

const zodiacSigns = [
  { key: 'aries', name: 'Aries', symbol: '♈', dates: '21 Mar - 19 Abr', image: 'https://customer-assets.emergentagent.com/job_a75c25da-ae07-48ea-bbf3-623d9056e132/artifacts/61pbrf4j_horoscope_white_01.png' },
  { key: 'taurus', name: 'Tauro', symbol: '♉', dates: '20 Abr - 20 May', image: 'https://customer-assets.emergentagent.com/job_a75c25da-ae07-48ea-bbf3-623d9056e132/artifacts/rv38u3ut_horoscope_white_02.png' },
  { key: 'gemini', name: 'Geminis', symbol: '♊', dates: '21 May - 20 Jun', image: 'https://customer-assets.emergentagent.com/job_a75c25da-ae07-48ea-bbf3-623d9056e132/artifacts/x42arsct_horoscope_white_03.png' },
  { key: 'cancer', name: 'Cancer', symbol: '♋', dates: '21 Jun - 22 Jul', image: 'https://customer-assets.emergentagent.com/job_a75c25da-ae07-48ea-bbf3-623d9056e132/artifacts/3v544m4b_horoscope_white_04.png' },
  { key: 'leo', name: 'Leo', symbol: '♌', dates: '23 Jul - 22 Ago', image: 'https://customer-assets.emergentagent.com/job_a75c25da-ae07-48ea-bbf3-623d9056e132/artifacts/aok4u1nc_horoscope_white_05.png' },
  { key: 'virgo', name: 'Virgo', symbol: '♍', dates: '23 Ago - 22 Sep', image: 'https://customer-assets.emergentagent.com/job_a75c25da-ae07-48ea-bbf3-623d9056e132/artifacts/vghuv3ks_horoscope_white_06.png' },
  { key: 'libra', name: 'Libra', symbol: '♎', dates: '23 Sep - 22 Oct', image: 'https://customer-assets.emergentagent.com/job_a75c25da-ae07-48ea-bbf3-623d9056e132/artifacts/b8drepsu_horoscope_white_07.png' },
  { key: 'scorpio', name: 'Escorpio', symbol: '♏', dates: '23 Oct - 21 Nov', image: 'https://customer-assets.emergentagent.com/job_a75c25da-ae07-48ea-bbf3-623d9056e132/artifacts/l7rcjjkw_horoscope_white_08.png' },
  { key: 'sagittarius', name: 'Sagitario', symbol: '♐', dates: '22 Nov - 21 Dic', image: 'https://customer-assets.emergentagent.com/job_a75c25da-ae07-48ea-bbf3-623d9056e132/artifacts/xq8tnly3_horoscope_white_09.png' },
  { key: 'capricorn', name: 'Capricornio', symbol: '♑', dates: '22 Dic - 19 Ene', image: 'https://customer-assets.emergentagent.com/job_a75c25da-ae07-48ea-bbf3-623d9056e132/artifacts/xdu88s1i_horoscope_white_10.png' },
  { key: 'aquarius', name: 'Acuario', symbol: '♒', dates: '20 Ene - 18 Feb', image: 'https://customer-assets.emergentagent.com/job_a75c25da-ae07-48ea-bbf3-623d9056e132/artifacts/avzhz2b4_horoscope_white_11.png' },
  { key: 'pisces', name: 'Piscis', symbol: '♓', dates: '19 Feb - 20 Mar', image: 'https://customer-assets.emergentagent.com/job_a75c25da-ae07-48ea-bbf3-623d9056e132/artifacts/fl8tjeeb_horoscope_white_12.png' },
];

export default function AstrologyPage() {
  const { user, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState('horoscope');
  const [selectedSign, setSelectedSign] = useState('');
  const [horoscope, setHoroscope] = useState(null);
  const [compatibility, setCompatibility] = useState(null);
  const [natalChart, setNatalChart] = useState(null);
  const [sign1, setSign1] = useState('');
  const [sign2, setSign2] = useState('');
  const [gender1, setGender1] = useState('mujer');
  const [gender2, setGender2] = useState('hombre');
  const [relationshipType, setRelationshipType] = useState('romantica');
  const [birthDate, setBirthDate] = useState('');
  const [birthTime, setBirthTime] = useState('');
  const [birthPlace, setBirthPlace] = useState('');
  const [birthGender, setBirthGender] = useState('');
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [perspectiveType, setPerspectiveType] = useState('Apparent Geocentric');
  const horoscopeResultRef = useRef(null);
  const compatibilityResultRef = useRef(null);
  const natalResultRef = useRef(null);

  const searchCities = async (query) => {
    if (query.length < 2) { setCitySuggestions([]); return; }
    try {
      const res = await fetch(`${API}/cities/search?q=${encodeURIComponent(query)}`);
      const data = await res.json();
      setCitySuggestions(data.cities || []);
      setShowCitySuggestions(true);
    } catch (e) {}
  };

  const selectCity = (city) => { setBirthPlace(city); setShowCitySuggestions(false); setCitySuggestions([]); };

  const getHoroscope = async (sign) => {
    setSelectedSign(sign); setLoading(true);
    try {
      const res = await fetch(`${API}/astrology/horoscope/${sign}`);
      setHoroscope(await res.json());
      setTimeout(() => horoscopeResultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (e) { toast.error('Error al obtener el horoscopo'); }
    finally { setLoading(false); }
  };

  const getCompatibility = async () => {
    if (!sign1 || !sign2) { toast.error('Selecciona ambos signos'); return; }
    setLoading(true); setCompatibility(null);
    try {
      const res = await fetch(`${API}/astrology/compatibility`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sign1, sign2, gender1, gender2, relationship_type: relationshipType })
      });
      setCompatibility(await res.json());
      setTimeout(() => compatibilityResultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (e) { toast.error('Error al obtener compatibilidad'); }
    finally { setLoading(false); }
  };

  const getNatalChart = async (fullChart = false) => {
    if (!birthDate) { toast.error('Ingresa tu fecha de nacimiento'); return; }
    if (fullChart && (!user || user.credits < 8)) { toast.error('Necesitas 8 creditos para la carta natal completa'); return; }
    setLoading(true); setNatalChart(null);
    try {
      const res = await fetch(`${API}/astrology/natal-chart`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ birth_date: birthDate, birth_time: birthTime, birth_place: birthPlace, birth_gender: birthGender, full_chart: fullChart, perspective_type: perspectiveType })
      });
      if (!res.ok) { toast.error((await res.json()).detail); return; }
      setNatalChart(await res.json());
      if (refreshUser) refreshUser();
      setTimeout(() => natalResultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (e) { toast.error('Error al generar carta natal'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl sm:text-4xl font-bold gold-text text-center mb-8" data-testid="astrology-title">Astrologia</h1>

        {/* Tabs */}
        <div className="flex justify-center mb-8 px-2">
          <div className="inline-flex bg-black/30 rounded-2xl p-1 border border-white/10 gap-1">
            {[{ key: 'horoscope', label: 'Horoscopo', icon: Sun }, { key: 'compatibility', label: 'Compatibilidad', icon: Heart }, { key: 'natal', label: 'Carta Natal', icon: Star }].map(({ key, label, icon: Icon }) => (
              <button key={key} onClick={() => setActiveTab(key)}
                className={`flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1.5 px-3 sm:px-5 py-2 rounded-xl text-xs sm:text-sm transition-all ${activeTab === key ? 'bg-gradient-to-r from-purple-600 to-purple-500 text-white shadow-lg' : 'text-[var(--text-muted)] hover:text-white'}`}
                data-testid={`tab-${key}`}
              ><Icon className="w-4 h-4 flex-shrink-0" /><span className="text-center leading-tight">{label}</span></button>
            ))}
          </div>
        </div>

        {/* Horoscope Tab */}
        {activeTab === 'horoscope' && (
          <div>
            <h2 className="text-xl font-bold text-center mb-6">Selecciona tu Signo</h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4 mb-8">
              {zodiacSigns.map((sign) => (
                <motion.button key={sign.key} whileHover={{ scale: 1.05 }} onClick={() => getHoroscope(sign.key)}
                  className={`glass-card p-3 text-center ${selectedSign === sign.key ? 'border-gold-300/15 glow-gold' : ''}`}
                  style={selectedSign === sign.key ? { borderColor: 'var(--purple-light)' } : {}} data-testid={`sign-${sign.key}`}>
                  <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-white/90 p-1.5 flex items-center justify-center">
                    <img src={sign.image} alt={sign.name} className="w-full h-full object-contain" />
                  </div>
                  <span className="text-sm font-bold block">{sign.name}</span>
                </motion.button>
              ))}
            </div>
            {loading && <div className="flex justify-center py-8" ref={horoscopeResultRef}><div className="spinner" /></div>}
            {horoscope && !loading && (
              <motion.div ref={horoscopeResultRef} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-20 h-20 rounded-full bg-white/90 p-2 flex items-center justify-center">
                    <img src={zodiacSigns.find(s => s.key === horoscope.sign)?.image} alt={horoscope.sign_es} className="w-full h-full object-contain" />
                  </div>
                  <div><h3 className="text-2xl font-bold gold-text">{horoscope.sign_es}</h3><p className="text-[var(--text-muted)]">Horoscopo Semanal - {horoscope.week}</p></div>
                </div>
                <MarkdownRenderer content={horoscope.horoscope} className="text-[var(--text-secondary)]" data-testid="horoscope-content" />
                <div className="mt-4 flex justify-end">
                  <ShareButton type="horoscope" content={{ sign: horoscope.sign_es, week: horoscope.week, horoscope: horoscope.horoscope, image: zodiacSigns.find(s => s.key === horoscope.sign)?.image }} title={`Horoscopo de ${horoscope.sign_es}`} description={`Horoscopo semanal - ${horoscope.week}`} />
                </div>
              </motion.div>
            )}
          </div>
        )}

        {/* Compatibility Tab */}
        {activeTab === 'compatibility' && (
          <div>
            <div className="glass-card p-8 max-w-2xl mx-auto mb-8">
              <h2 className="text-xl font-bold text-center mb-6">Compatibilidad Zodiacal</h2>
              <div className="grid sm:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm text-[var(--text-muted)] mb-2">Persona 1</label>
                  <div className="flex gap-2 mb-2">
                    {['mujer', 'hombre'].map(g => (
                      <button key={g} onClick={() => setGender1(g)} className={`flex-1 py-2 px-3 rounded-lg text-sm transition-all ${gender1 === g ? (g === 'mujer' ? 'bg-pink-500/30 border border-pink-400 text-pink-300' : 'bg-blue-500/30 border border-blue-400 text-blue-300') : 'bg-black/30 border border-white/10 text-[var(--text-muted)] hover:text-white'}`}>
                        {g === 'mujer' ? '♀ Mujer' : '♂ Hombre'}
                      </button>
                    ))}
                  </div>
                  <select value={sign1} onChange={e => setSign1(e.target.value)} className="mystic-input" data-testid="sign1-select">
                    <option value="">Seleccionar signo...</option>
                    {zodiacSigns.map(s => <option key={s.key} value={s.key}>{s.symbol} {s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-[var(--text-muted)] mb-2">Persona 2</label>
                  <div className="flex gap-2 mb-2">
                    {['mujer', 'hombre'].map(g => (
                      <button key={g} onClick={() => setGender2(g)} className={`flex-1 py-2 px-3 rounded-lg text-sm transition-all ${gender2 === g ? (g === 'mujer' ? 'bg-pink-500/30 border border-pink-400 text-pink-300' : 'bg-blue-500/30 border border-blue-400 text-blue-300') : 'bg-black/30 border border-white/10 text-[var(--text-muted)] hover:text-white'}`}>
                        {g === 'mujer' ? '♀ Mujer' : '♂ Hombre'}
                      </button>
                    ))}
                  </div>
                  <select value={sign2} onChange={e => setSign2(e.target.value)} className="mystic-input" data-testid="sign2-select">
                    <option value="">Seleccionar signo...</option>
                    {zodiacSigns.map(s => <option key={s.key} value={s.key}>{s.symbol} {s.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-sm text-[var(--text-muted)] mb-2 text-center">Tipo de Relacion</label>
                <div className="flex gap-2">
                  {[{ key: 'romantica', icon: '❤️', label: 'Romantica', color: 'red' }, { key: 'amistad', icon: '🤝', label: 'Amistad', color: 'yellow' }, { key: 'trabajo', icon: '💼', label: 'Trabajo', color: 'green' }].map(r => (
                    <button key={r.key} onClick={() => setRelationshipType(r.key)}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm transition-all flex items-center justify-center gap-1 ${relationshipType === r.key ? `bg-${r.color}-500/30 border border-${r.color}-400 text-${r.color}-300` : 'bg-black/30 border border-white/10 text-[var(--text-muted)] hover:text-white'}`}>
                      {r.icon} {r.label}
                    </button>
                  ))}
                </div>
              </div>
              <button onClick={getCompatibility} disabled={loading} className="btn-gold w-full" data-testid="check-compatibility-btn">
                {loading ? <span className="spinner w-5 h-5 mx-auto" /> : 'Ver Compatibilidad'}
              </button>
            </div>
            {loading && <div className="flex justify-center py-8" ref={compatibilityResultRef}><div className="spinner" /></div>}
            {compatibility && !loading && (
              <motion.div ref={compatibilityResultRef} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card p-8 max-w-2xl mx-auto">
                <div className="flex justify-center items-center gap-4 mb-6">
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-full bg-white/90 p-1.5 flex items-center justify-center mx-auto">
                      <img src={zodiacSigns.find(s => s.key === sign1)?.image} alt={sign1} className="w-full h-full object-contain" />
                    </div>
                    <span className={`text-xs mt-1 block ${gender1 === 'mujer' ? 'text-pink-400' : 'text-blue-400'}`}>{gender1 === 'mujer' ? '♀' : '♂'} {compatibility.sign1}</span>
                  </div>
                  <Heart className="w-8 h-8 text-purple-400" />
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-full bg-white/90 p-1.5 flex items-center justify-center mx-auto">
                      <img src={zodiacSigns.find(s => s.key === sign2)?.image} alt={sign2} className="w-full h-full object-contain" />
                    </div>
                    <span className={`text-xs mt-1 block ${gender2 === 'mujer' ? 'text-pink-400' : 'text-blue-400'}`}>{gender2 === 'mujer' ? '♀' : '♂'} {compatibility.sign2}</span>
                  </div>
                </div>
                <MarkdownRenderer content={compatibility.analysis} className="text-[var(--text-secondary)]" data-testid="compatibility-content" />
                <div className="mt-4 flex justify-end">
                  <ShareButton type="compatibility" content={{ sign1: compatibility.sign1, sign2: compatibility.sign2, analysis: compatibility.analysis, relationship: relationshipType }} title={`Compatibilidad: ${compatibility.sign1} y ${compatibility.sign2}`} description="Descubre la compatibilidad entre signos del zodiaco" />
                </div>
              </motion.div>
            )}
          </div>
        )}

        {/* Natal Chart Tab */}
        {activeTab === 'natal' && (
          <div>
            <div className="glass-card p-8 max-w-2xl mx-auto mb-8">
              <h2 className="text-xl font-bold text-center mb-6">Tu Carta Natal</h2>
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm text-[var(--text-muted)] mb-2">Fecha de Nacimiento *</label>
                  <input type="date" value={birthDate} onChange={e => setBirthDate(e.target.value)} className="mystic-input" data-testid="birth-date-input" />
                </div>
                <div>
                  <label className="block text-sm text-[var(--text-muted)] mb-2">Hora de Nacimiento (para ascendente)</label>
                  <input type="time" value={birthTime} onChange={e => setBirthTime(e.target.value)} className="mystic-input" data-testid="birth-time-input" />
                </div>
                <div className="relative">
                  <label className="block text-sm text-[var(--text-muted)] mb-2">Lugar de Nacimiento</label>
                  <input type="text" value={birthPlace} onChange={e => { setBirthPlace(e.target.value); searchCities(e.target.value); }}
                    onFocus={() => citySuggestions.length > 0 && setShowCitySuggestions(true)}
                    onBlur={() => setTimeout(() => setShowCitySuggestions(false), 200)}
                    placeholder="Escribe para buscar ciudad..." className="mystic-input" data-testid="birth-place-input" />
                  {showCitySuggestions && citySuggestions.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-[var(--bg-secondary)] border border-gold-300/10 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {citySuggestions.map((city, i) => (
                        <button key={i} onClick={() => selectCity(city)} className="w-full text-left px-4 py-2 text-sm text-[var(--text-secondary)] hover:bg-purple-400/10 hover:text-white transition-colors">{city}</button>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-[var(--text-muted)] mb-2">Sexo</label>
                  <div className="grid grid-cols-2 gap-3">
                    {[{ value: 'mujer', label: 'Mujer' }, { value: 'hombre', label: 'Hombre' }].map(opt => (
                      <button key={opt.value} onClick={() => setBirthGender(opt.value)}
                        className={`py-2 px-4 rounded-lg border text-sm font-medium transition-colors ${birthGender === opt.value ? 'border-gold-300/15 bg-purple-400/10 text-white' : 'border-gold-300/10 bg-black/30 text-[var(--text-muted)] hover:border-gold-300/20'}`}
                        data-testid={`gender-${opt.value}`}>{opt.label}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-[var(--text-muted)] mb-2">Perspectiva de Calculo</label>
                  <div className="grid grid-cols-2 gap-3">
                    {[{ value: 'Apparent Geocentric', label: 'Geocentrica' }, { value: 'Topocentric', label: 'Topocentrica' }].map(opt => (
                      <button key={opt.value} onClick={() => setPerspectiveType(opt.value)}
                        className={`py-2 px-4 rounded-lg border text-sm font-medium transition-colors ${perspectiveType === opt.value ? 'border-gold-300/15 bg-purple-400/10 text-white' : 'border-gold-300/10 bg-black/30 text-[var(--text-muted)] hover:border-gold-300/20'}`}
                        data-testid={`perspective-${opt.value === 'Apparent Geocentric' ? 'geocentric' : 'topocentric'}`}>{opt.label}</button>
                    ))}
                  </div>
                  <p className="text-[10px] text-[var(--text-muted)] mt-1.5">Geocentrica: vista desde el centro de la Tierra. Topocentrica: desde tu lugar exacto de nacimiento.</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <button onClick={() => getNatalChart(false)} disabled={loading} className="btn-ghost" data-testid="basic-chart-btn">
                  {loading ? <span className="flex items-center justify-center gap-2"><span className="spinner w-4 h-4" /> Generando...</span> : 'Carta Basica (Gratis)'}
                </button>
                <button onClick={() => getNatalChart(true)} disabled={loading || !user || user?.credits < 8} className="btn-gold flex items-center justify-center gap-2" data-testid="full-chart-btn">
                  {loading ? <span className="flex items-center justify-center gap-2"><span className="spinner w-4 h-4" /> Generando...</span> : <><Coins className="w-4 h-4" /> Carta Completa (8)</>}
                </button>
              </div>
              {!user && <p className="text-center text-[var(--text-muted)] text-sm mt-4">Inicia sesion para acceder a la carta natal completa</p>}
            </div>
            {loading && <div className="flex justify-center py-8" ref={natalResultRef}><div className="spinner" /></div>}
            {natalChart && !loading && (
              <motion.div ref={natalResultRef} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card p-6 sm:p-8 max-w-4xl mx-auto">
                <div className="flex items-center gap-4 mb-6 pb-4 border-b border-gold-300/10">
                  <div>
                    <h3 className="text-xl font-bold gold-text">Carta Natal {natalChart.chart_type}</h3>
                    <p className="text-[var(--text-muted)] text-sm">Signo Solar: {natalChart.sun_sign_es} | {natalChart.birth_date} {natalChart.birth_time && `| ${natalChart.birth_time}`} {natalChart.birth_place && `| ${natalChart.birth_place}`}</p>
                    <p className="text-purple-400 text-xs mt-1">Posiciones calculadas con efemérides suizas {natalChart.perspective_type && natalChart.perspective_type !== 'Apparent Geocentric' ? `| Perspectiva: ${natalChart.perspective_type === 'Topocentric' ? 'Topocéntrica' : natalChart.perspective_type}` : ''}</p>
                  </div>
                </div>

                {/* Natal Chart Wheel - Full Width */}
                {natalChart.chart_data && (
                  <div className="flex flex-col items-center mb-6">
                    <h4 className="text-lg font-semibold text-purple-300 mb-4">Rueda Zodiacal</h4>
                    <NatalChartWheel chartData={natalChart.chart_data} aspects={natalChart.aspects} />
                  </div>
                )}

                {/* Planetary Positions */}
                {natalChart.planets && (
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-purple-300 mb-3">Posiciones Planetarias</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {natalChart.planets.map((planet, i) => (
                        <div key={i} className="flex justify-between items-center bg-black/30 rounded-lg px-3 py-2 border border-gold-300/8 min-w-0 overflow-hidden gap-2">
                          <span className="text-[var(--text-secondary)] text-xs sm:text-sm font-medium truncate shrink-0">{planet.name}</span>
                          <div className="flex items-center gap-1 sm:gap-2 min-w-0">
                            <span className="text-white font-medium text-xs sm:text-sm truncate">{planet.sign}</span>
                            {planet.position && <span className="text-purple-400 text-[10px] sm:text-xs shrink-0">{planet.position}°</span>}
                            {planet.retrograde && <span className="text-red-400 text-[10px] sm:text-xs font-bold shrink-0">R</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Houses (for full chart) */}
                {natalChart.houses && natalChart.houses.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-purple-300 mb-3">Casas Astrológicas</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                      {natalChart.houses.map((house, i) => (
                        <div key={i} className="bg-black/30 rounded-lg px-2 py-2 border border-gold-300/8 text-center overflow-hidden">
                          <span className="text-purple-400 text-[10px] sm:text-xs block">Casa {house.number}</span>
                          <span className="text-white font-medium text-xs sm:text-sm truncate block">{house.sign}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Smart Aspects Table */}
                {natalChart.aspects && natalChart.aspects.length > 0 && (
                  <AspectsTable aspects={natalChart.aspects} />
                )}

                {natalChart.analysis && (
                  <div>
                    <h4 className="text-lg font-semibold text-purple-300 mb-3">{natalChart.chart_type === 'completa' ? 'Análisis Completo' : 'Interpretación'}</h4>
                    <MarkdownRenderer content={natalChart.analysis} className="text-[var(--text-secondary)] text-sm" data-testid="natal-chart-content" />
                  </div>
                )}
                {natalChart.chart_type === 'básica' && (
                  <div className="mt-6 p-4 bg-purple-500/10 border border-gold-300/10 rounded-lg text-center">
                    <p className="text-[var(--text-secondary)] text-sm mb-2">¿Quieres una interpretacion mas profunda?</p>
                    <p className="text-purple-300 text-xs">La carta completa incluye un analisis detallado de cada planeta en su casa, aspectos clave, balance elemental y consejo evolutivo personalizado.</p>
                  </div>
                )}
                <div className="mt-4 flex justify-end">
                  <ShareButton type="natal_chart" content={{ sun_sign: natalChart.sun_sign_es, chart_type: natalChart.chart_type, birth_date: natalChart.birth_date, planets: natalChart.planets, houses: natalChart.houses, analysis: natalChart.analysis, chart_data: natalChart.chart_data, aspects: natalChart.aspects }} title={`Carta Natal: ${natalChart.sun_sign_es}`} description="Descubre tu mapa astral personalizado" />
                </div>
              </motion.div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
